/** LISTA DE CANTITĂȚI (BoQ): tipurile, clientul API și funcțiile pure ale
 *  ecranului (filtrarea pe subiecte, totalurile, calea exportului).
 *
 *  Lista o calculează serverul (`/api/projects/:id/solutions/:sol/boq`), pe
 *  motorul din `cost-model/cost_model/boq`, și o păstrează pe soluție; aici
 *  nu se recalculează nimic — se arată, se filtrează și se exportă. Catalogul
 *  (rețete, resurse) are două straturi: cel GLOBAL, din cod, și COPIILE
 *  proiectului, pe care le scrie omul (`putCatalogBoq`); ecranul le arată
 *  împreună, cu ce e al proiectului marcat.
 *
 *  Un SCENARIU poate schimba și el catalogul: ține numai suprascrierile față
 *  de catalogul proiectului (`Suprascrieri`, pe `Scenariu.catalog`), iar
 *  lista lui o calculează serverul cu ele peste (`?scenariu=<id>`). Aici
 *  sunt cele două sensuri, aceleași ca pe server (`api/boq.py::suprapune`):
 *  `suprapune` (proiect + scenariu → ce vede ecranul) și `suprascrierile`
 *  (ce a schimbat omul pe ecran → ce se ține pe scenariu). */
import { get, put } from "../../api";
import { t } from "../../i18n";

export type TipResursa = "material" | "manopera" | "utilaj" | "transport";
export type FelCantitate = "calculat" | "estimat" | "solutie" | "om";

export type Consum = { tip: TipResursa; cod: string; cantitate: number };

export type Reteta = {
  cod: string; subiect: string; capitol: string; nume: string; um: string;
  cheie: string | null; consumuri: Consum[]; specificatii?: string; observatii?: string;
  sursa?: string; din_proiect?: boolean;
  /** prețul unitar de-a gata al lucrării (dintr-o ofertă), EUR/UM */
  pret_impus?: number;
  /** cheile din care iese cantitatea, puse pe proiect una câte una
   *  (macaralele × lunile, `metraj.FACTORI`) */
  factori?: string[];
  /** rețetă de REZERVĂ (pe m², pe buc): intră în listă numai cât lipsește sursa
   *  detaliată, numită aici (`catalog.INLOCUITE_DE`) */
  inlocuit_de?: string;
};

export type Resursa = {
  cod: string; nume: string; um?: string; pret?: number; pret_ora?: number;
  categorie?: string; sursa?: string; nota?: string; din_proiect?: boolean;
  /** utilajul se plătește numai în rețeta asta (macaraua, în chiria ei); în
   *  celelalte apare la consumuri cu cost zero */
  costul_in?: string;
  /** prețul a venit dintr-un import din piață (`piataResurse.ts`); o editare de
   *  mână îl scoate (`cuPretDeOm`) */
  piata?: MarcaPiata;
};

/** Ce ține minte o țintă despre prețul pieței pus pe ea: cheia, prețul în lei,
 *  când s-a văzut și produsul — de aici panoul spune mai târziu cât s-a mișcat
 *  piața de la import. Forma o scrie serverul; aici trece neatinsă. */
export type MarcaPiata = { cheie: string; [k: string]: unknown };

export type Resurse = {
  materiale: Record<string, Resursa>; manopera: Record<string, Resursa>;
  utilaje: Record<string, Resursa>; transport: Record<string, Resursa>;
};
export type ListaResurse = keyof Resurse;
export const LISTE_RESURSE: ListaResurse[] = ["materiale", "manopera", "utilaje", "transport"];
export const TIP_LA_LISTA: Record<TipResursa, ListaResurse> = {
  material: "materiale", manopera: "manopera", utilaj: "utilaje", transport: "transport",
};

export type CatalogGlobal = {
  versiune: string; moneda: string;
  subiecte: { cod: string; nume: string }[];
  capitole: Record<string, { cod: string; nume: string }[]>;
  resurse: Resurse; retete: Reteta[];
};

/** Copiile proiectului: ce a schimbat omul peste global. */
export type CopiiProiect = {
  retete: Record<string, Partial<Reteta>>;
  resurse: Partial<Record<ListaResurse, Record<string, Partial<Resursa>>>>;
  cantitati: Record<string, number>;
  ascunse: string[];
  /** prețurile impuse articolelor devizelor (structură, fațadă), pe
   *  `Articol.cheie_pret`: pe proiect, din importul prețurilor de piață; pe
   *  scenarii, din oferte. Pagina de rețete nu le editează, dar le poartă
   *  neatinse la salvare — altfel o salvare ar șterge prețurile pieței. */
  articole?: Record<string, PretArticol>;
};
export type PretArticol = { material?: number; total?: number; sursa?: string; piata?: MarcaPiata };

/** Ce schimbă un scenariu în catalogul proiectului. `ascunse` lipsă = ale
 *  proiectului. */
export type Suprascrieri = Partial<CopiiProiect>;
export type CatalogProiect = { rev: number; data: CopiiProiect };
export type CatalogBoq = { global: CatalogGlobal; proiect: CatalogProiect };

export type Bani = { material: number; manopera: number; utilaj: number; transport: number; total: number };

export type RandResursa = {
  tip: TipResursa; cod: string; nume: string; um: string; consum: number; cantitate: number;
  pret_unitar: number; total_um: number; total: number; sursa?: string; din_proiect?: boolean; lipsa?: boolean;
};

export type Articol = {
  nr: string; cod: string; reteta: string | null; subiect: string; capitol: string;
  denumire: string; specificatii: string; um: string; cantitate: number;
  sursa_cantitate: { fel: FelCantitate; explicatie?: string; precizie?: string; calculata?: number | null };
  pe_corp: Record<string, number>;
  // corp → nivel → cantitate, pentru programul de execuție; lipsește pe lista compactă
  pe_nivel?: Record<string, Record<string, number>>;
  pu: Bani; total: Bani; observatii: string; sursa_pret: string;
  resurse: RandResursa[]; din_proiect: boolean; resurse_lipsa: string[];
  din_deviz: "structura" | "fatada" | null; componenta?: boolean; cheie_fatada?: string;
  /** cheia articolului unui deviz pentru un preț impus (`motor.cheie_pret`) */
  cheie_pret?: string;
  /** prețul unitar vine de-a gata (o ofertă); `inainte` = cel calculat */
  pret_impus?: { inainte: number; sursa: string };
  /** valorile factorilor rețetei, cum au intrat în cantitate */
  factori?: Record<string, number>;
  /** faza proiectului = etapa programului de execuție (`etape.etapa_articolului`);
   *  `null` la macarale, pe care programul le socotește singur */
  etapa?: string | null;
};

export type Capitol = { cod: string; nr: string; nume: string; articole: Articol[]; total: Bani };
export type Subiect = {
  cod: string; nr: string; nume: string; capitole: Capitol[]; total: Bani;
  articole: number; din_deviz: boolean; estimate: number;
};

export type ListaCantitati = {
  versiune: number; versiune_catalog: string; moneda: string; generat_la: string;
  meta: Record<string, unknown>;
  context: {
    corpuri: string[]; apartamente: number; apartamente_pe_camere: Record<string, number>;
    niveluri_subsol: number; arie_parcaj_m2: number; arie_desfasurata_m2: number;
    scari?: Record<string, number>; lifturi?: Record<string, number>;
    subsol?: { arie_m2: number; perimetru_m: number; adancime_m: number };
  };
  subiecte: Subiect[];
  total_direct: Bani;
  indirecte: { cheie: string; procent: number; baza: number; valoare: number }[];
  subtotal: number; tva_pct: number; total_general: number;
  arie_desfasurata_m2: number | null; eur_pe_m2: number | null;
  neaplicate: { cod: string; denumire: string; subiect: string; capitol: string; um: string; cheie: string | null; motiv: string }[];
  statistica: { articole: number; estimate: number; puse_de_om: number; din_devize: number; resurse_lipsa: string[] };
  note: string[]; amprenta: string; amprenta_intrari?: string; durata_s?: number;
  /** fazele proiectului, în ordinea programului (listele de dinainte de v5 nu le au) */
  etape?: Etapa[];
};

export type Etapa = { cod: string; nume: string };

/** FAZELE în care consumă proiectul fiecare resursă (`lista:cod`): etapele
 *  programului ale articolelor care o folosesc, cu costul ei pe fiecare, cea
 *  mai scumpă întâi. O resursă nefolosită de listă nu e în hartă. */
export function fazeleResurselor(lista: ListaCantitati | null): Map<string, { etapa: string | null; cost: number }[]> {
  const pe = new Map<string, Map<string | null, number>>();
  for (const s of lista?.subiecte ?? []) for (const c of s.capitole) for (const a of c.articole) {
    for (const x of a.resurse ?? []) {
      const l = TIP_LA_LISTA[x.tip];
      if (!l || x.lipsa) continue;
      const k = `${l}:${x.cod}`;
      const m = pe.get(k) ?? new Map<string | null, number>();
      const e = a.etapa ?? null;
      m.set(e, (m.get(e) ?? 0) + (x.total ?? 0));
      pe.set(k, m);
    }
  }
  return new Map([...pe].map(([k, m]) => [k, [...m].map(([etapa, cost]) => ({ etapa, cost }))
    .sort((a, b) => b.cost - a.cost)]));
}

/** Numele fazei, ca în fila «Program»; fără fază: macaralele (programul le
 *  socotește singur) și ce lista nu folosește. */
export function numeFaza(etape: Etapa[], cod: string | null): string {
  return (cod != null && etape.find((e) => e.cod === cod)?.nume) || t("lc.res.fara_faza");
}

/** Rândurile grupate pe faze, în ordinea programului; ce n-are fază (nefolosit
 *  de listă, macaralele) la sfârșit, în grupul `null`. */
export function grupatePeFaze<T>(randuri: T[], fazaLui: (r: T) => string | null, etape: Etapa[]): { etapa: string | null; randuri: T[] }[] {
  const ordine = new Map(etape.map((e, i) => [e.cod, i]));
  const grupuri = new Map<string | null, T[]>();
  for (const r of randuri) {
    const e = fazaLui(r);
    const cheie = e != null && ordine.has(e) ? e : null;
    grupuri.set(cheie, [...(grupuri.get(cheie) ?? []), r]);
  }
  return [...grupuri].map(([etapa, rr]) => ({ etapa, randuri: rr }))
    .sort((a, b) => (a.etapa == null ? 1e9 : ordine.get(a.etapa)!) - (b.etapa == null ? 1e9 : ordine.get(b.etapa)!));
}

// --- API -------------------------------------------------------------------

const cale = (projectId: string) => `/api/projects/${projectId}`;

/** Lista soluției; cu `scenariu`, a scenariului (catalogul lui peste al
 *  proiectului). `compact` = fără desfacerea pe resurse și corpuri, destul
 *  pentru rezumat (comparația scenariilor). */
export const getListaCantitati = (projectId: string, solutionId: string, refa = false,
                                  opt: { scenariu?: string | null; compact?: boolean } = {}) => {
  const q = new URLSearchParams();
  if (refa) q.set("refa", "1");
  if (opt.scenariu) q.set("scenariu", opt.scenariu);
  if (opt.compact) q.set("compact", "1");
  const s = q.toString();
  return get<ListaCantitati>(`${cale(projectId)}/solutions/${solutionId}/boq${s ? `?${s}` : ""}`);
};

export const getCatalogBoq = (projectId: string) => get<CatalogBoq>(`${cale(projectId)}/costuri/boq/catalog`);

export const putCatalogBoq = (projectId: string, rev: number, data: CopiiProiect) =>
  put<CatalogProiect>(`${cale(projectId)}/costuri/boq/catalog`, { rev, data });

/** Adresa fișierului Excel: toată lista sau numai subiectele alese. Descărcarea
 *  e o navigare obișnuită (cookie-ul de sesiune pleacă singur). */
export function caleExport(projectId: string, solutionId: string, subiecte: string[] = [],
                           scenariu: string | null = null): string {
  const q = new URLSearchParams();
  if (subiecte.length) q.set("subiecte", subiecte.join(","));
  if (scenariu) q.set("scenariu", scenariu);
  const s = q.toString();
  return `${cale(projectId)}/solutions/${solutionId}/boq/export.xlsx${s ? `?${s}` : ""}`;
}

// --- funcții pure ------------------------------------------------------------

export const BANI_ZERO: Bani = { material: 0, manopera: 0, utilaj: 0, transport: 0, total: 0 };

export function adunaBani(a: Bani, b: Bani): Bani {
  return { material: a.material + b.material, manopera: a.manopera + b.manopera,
           utilaj: a.utilaj + b.utilaj, transport: a.transport + b.transport, total: a.total + b.total };
}

/** Subiectele arătate: cele alese, sau toate când nu e ales nimic. */
export function subiecteVazute(lista: ListaCantitati, alese: Set<string>): Subiect[] {
  return lista.subiecte.filter((s) => alese.size === 0 || alese.has(s.cod));
}

/** Totalul subiectelor arătate, direct, și câte articole sunt. */
export function totalVazut(subiecte: Subiect[]): { total: Bani; articole: number; estimate: number } {
  return subiecte.reduce(
    (acc, s) => ({ total: adunaBani(acc.total, s.total), articole: acc.articole + s.articole, estimate: acc.estimate + s.estimate }),
    { total: BANI_ZERO, articole: 0, estimate: 0 },
  );
}

/** Articolele unui subiect care se potrivesc cu textul căutat (cod, denumire,
 *  specificații), cu capitolele goale scoase. */
export function cauta(subiect: Subiect, text: string): Capitol[] {
  const q = text.trim().toLowerCase();
  if (!q) return subiect.capitole;
  return subiect.capitole
    .map((c) => ({ ...c, articole: c.articole.filter((a) =>
      [a.nr, a.cod, a.denumire, a.specificatii, a.observatii].some((x) => (x ?? "").toLowerCase().includes(q))) }))
    .filter((c) => c.articole.length > 0);
}

/** Rețeta așa cum o vede proiectul: globalul cu copia proiectului peste. */
export function retetaProiectului(glob: CatalogGlobal, copii: CopiiProiect, cod: string): Reteta | null {
  const g = glob.retete.find((r) => r.cod === cod);
  const c = copii.retete[cod];
  if (!g && !c) return null;
  return { ...(g ?? { cod, subiect: "", capitol: "", nume: "", um: "", cheie: null, consumuri: [] }),
           ...(c ?? {}), cod, din_proiect: !!c } as Reteta;
}

export function resursaProiectului(glob: CatalogGlobal, copii: CopiiProiect, lista: ListaResurse, cod: string): Resursa | null {
  const g = glob.resurse[lista]?.[cod];
  const c = copii.resurse[lista]?.[cod];
  if (!g && !c) return null;
  return { ...(g ?? { cod, nume: cod }), ...(c ?? {}), cod, din_proiect: !!c } as Resursa;
}

/** Prețul unitar al unei rețete pe catalogul proiectului, desfăcut — ca
 *  editorul să arate pe loc ce schimbă un consum sau un preț. */
/** Resursa se plătește în altă rețetă (`costul_in`), ca pe server (`motor.pret_unitar`). */
export const platitAltundeva = (res: Resursa, r: Reteta) => !!res.costul_in && res.costul_in !== r.cod;

export function pretUnitarReteta(glob: CatalogGlobal, copii: CopiiProiect, r: Reteta): Bani & { lipsa: string[] } {
  const pu = { ...BANI_ZERO, lipsa: [] as string[] };
  for (const c of r.consumuri) {
    const res = resursaProiectului(glob, copii, TIP_LA_LISTA[c.tip], c.cod);
    if (!res) { pu.lipsa.push(c.cod); continue; }
    if (platitAltundeva(res, r)) continue;
    const pret = c.tip === "manopera" || c.tip === "utilaj" ? (res.pret_ora ?? 0) : (res.pret ?? 0);
    pu[c.tip] += c.cantitate * pret;
  }
  pu.total = pu.material + pu.manopera + pu.utilaj + pu.transport;
  // prețul impus ține desfacerea calculată, ca pe server (`motor._impune_pret`)
  if (r.pret_impus != null && Number.isFinite(r.pret_impus)) {
    const f = pu.total > 0 ? r.pret_impus / pu.total : 0;
    for (const k of ["material", "manopera", "utilaj", "transport"] as const) pu[k] = f ? pu[k] * f : 0;
    if (!f) pu.material = r.pret_impus;
    pu.total = r.pret_impus;
  }
  return pu;
}

/** Copiile cu rețeta `cod` pusă (sau scoasă, cu `null`). */
export function cuReteta(copii: CopiiProiect, cod: string, r: Partial<Reteta> | null): CopiiProiect {
  const retete = { ...copii.retete };
  if (r === null) delete retete[cod]; else retete[cod] = r;
  return { ...copii, retete };
}

export function cuResursa(copii: CopiiProiect, lista: ListaResurse, cod: string, r: Partial<Resursa> | null): CopiiProiect {
  const aLista = { ...(copii.resurse[lista] ?? {}) };
  if (r === null) delete aLista[cod]; else aLista[cod] = r;
  const resurse = { ...copii.resurse, [lista]: aLista };
  if (Object.keys(aLista).length === 0) delete resurse[lista];
  return { ...copii, resurse };
}

export function cuCantitate(copii: CopiiProiect, cod: string, v: number | null): CopiiProiect {
  const cantitati = { ...copii.cantitati };
  if (v === null) delete cantitati[cod]; else cantitati[cod] = v;
  return { ...copii, cantitati };
}

export function cuAscunsa(copii: CopiiProiect, cod: string, ascunsa: boolean): CopiiProiect {
  const set = new Set(copii.ascunse);
  if (ascunsa) set.add(cod); else set.delete(cod);
  return { ...copii, ascunse: [...set] };
}

export const COPII_GOALE: CopiiProiect = { retete: {}, resurse: {}, cantitati: {}, ascunse: [] };

/** Ce poate schimba omul la o rețetă: numele, textele, unitatea, consumurile. */
const CAMPURI_RETETA = ["nume", "specificatii", "observatii", "um", "subiect", "capitol", "consumuri"] as const;

/** Copia proiectului s-a îndepărtat de rețeta globală cu același cod? O
 *  rețetă care nu există în global e, prin definiție, a proiectului. */
export function retetaDifera(glob: CatalogGlobal, copie: Partial<Reteta>, cod: string): boolean {
  const g = glob.retete.find((r) => r.cod === cod);
  if (!g) return true;
  return CAMPURI_RETETA.some((k) => k in copie && JSON.stringify(copie[k] ?? null) !== JSON.stringify(g[k] ?? null));
}

export function resursaDifera(glob: CatalogGlobal, lista: ListaResurse, copie: Partial<Resursa>, cod: string): boolean {
  const g = glob.resurse[lista]?.[cod];
  if (!g) return true;
  return (["pret", "pret_ora", "nume", "um"] as const).some((k) => k in copie && copie[k] !== g[k]);
}

/** Copia globalului, ca punct de pornire al proiectului. */
export function cloneazaReteta(glob: CatalogGlobal, cod: string): Partial<Reteta> | null {
  const g = glob.retete.find((r) => r.cod === cod);
  return g ? JSON.parse(JSON.stringify(g)) as Reteta : null;
}

export function cloneazaResursa(glob: CatalogGlobal, lista: ListaResurse, cod: string): Partial<Resursa> | null {
  const g = glob.resurse[lista]?.[cod];
  return g ? { ...g } : null;
}

/** Catalogul proiectului cu suprascrierile scenariului peste: rețetă cu
 *  rețetă, resursă cu resursă, întregi (ca `api/boq.py::suprapune`). */
export function suprapune(baza: CopiiProiect, peste: Suprascrieri | null | undefined): CopiiProiect {
  if (!peste) return baza;
  const resurse: CopiiProiect["resurse"] = { ...baza.resurse };
  for (const l of LISTE_RESURSE) {
    if (peste.resurse?.[l]) resurse[l] = { ...(baza.resurse[l] ?? {}), ...peste.resurse[l] };
  }
  // prețurile impuse articolelor: ale proiectului (din piață) cu ale scenariului
  // (din oferte) peste, articol cu articol — o ofertă bate piața pe ce acoperă
  const articole = { ...(baza.articole ?? {}), ...(peste.articole ?? {}) };
  return {
    retete: { ...baza.retete, ...(peste.retete ?? {}) }, resurse,
    cantitati: { ...baza.cantitati, ...(peste.cantitati ?? {}) },
    ascunse: peste.ascunse ?? baza.ascunse,
    ...(Object.keys(articole).length ? { articole } : {}),
  };
}

const la = (v: unknown) => JSON.stringify(v ?? null);

/** Ce diferă `efectiv` de catalogul proiectului — suprascrierile de ținut pe
 *  scenariu. O rețetă sau o resursă schimbată se ține întreagă; ce e egal cu
 *  proiectul nu se ține, ca scenariul să urmeze proiectul în rest. La fel
 *  prețurile impuse articolelor: cele ale scenariului (din oferte) rămân pe el,
 *  cele venite din proiect (din piață) nu se copiază pe scenariu — altfel un
 *  import nou în proiect n-ar mai ajunge la scenariu. */
export function suprascrierile(baza: CopiiProiect, efectiv: CopiiProiect): Suprascrieri {
  const out: Suprascrieri = {};
  const retete: CopiiProiect["retete"] = {};
  for (const [cod, r] of Object.entries(efectiv.retete)) if (la(r) !== la(baza.retete[cod])) retete[cod] = r;
  if (Object.keys(retete).length) out.retete = retete;
  const resurse: CopiiProiect["resurse"] = {};
  for (const l of LISTE_RESURSE) {
    const m: Record<string, Partial<Resursa>> = {};
    for (const [cod, r] of Object.entries(efectiv.resurse[l] ?? {})) if (la(r) !== la(baza.resurse[l]?.[cod])) m[cod] = r;
    if (Object.keys(m).length) resurse[l] = m;
  }
  if (Object.keys(resurse).length) out.resurse = resurse;
  const cantitati: Record<string, number> = {};
  for (const [cod, v] of Object.entries(efectiv.cantitati)) if (baza.cantitati[cod] !== v) cantitati[cod] = v;
  if (Object.keys(cantitati).length) out.cantitati = cantitati;
  if (la([...efectiv.ascunse].sort()) !== la([...baza.ascunse].sort())) out.ascunse = [...efectiv.ascunse];
  const articole: Record<string, PretArticol> = {};
  for (const [k, a] of Object.entries(efectiv.articole ?? {})) if (la(a) !== la(baza.articole?.[k])) articole[k] = a;
  if (Object.keys(articole).length) out.articole = articole;
  return out;
}

/** Prețul pus de om pe o resursă. Dacă prețul de dinainte venea din piață,
 *  copia își pierde marca pieței (`piata`) și eticheta ei (`sursa`): serverul
 *  n-o mai ia drept preț de piață (pe care un import nou l-ar rescrie din
 *  oficiu), iar ecranul nu mai scrie lângă cifra omului sursa altei cifre. */
export function cuPretDeOm(copie: Partial<Resursa>, camp: "pret" | "pret_ora", v: number): Partial<Resursa> {
  const { piata, ...rest } = copie;
  if (piata) delete rest.sursa;
  return { ...rest, [camp]: v };
}

/** Câte lucruri schimbă scenariul în catalog. */
export function nrSuprascrieri(s: Suprascrieri | null | undefined): number {
  if (!s) return 0;
  return Object.keys(s.retete ?? {}).length + Object.keys(s.cantitati ?? {}).length
    + Object.keys(s.articole ?? {}).length + (s.ascunse ? 1 : 0)
    + LISTE_RESURSE.reduce((n, l) => n + Object.keys(s.resurse?.[l] ?? {}).length, 0);
}
